<template>
  <CustomDropdown
    ref="dropdownRef"
    maxHeight="300px"
    gap="10px"
    position="start"
    dropdownCSS="p-0"
    :disabled="disabled"
    :relative-to-viewport="true"
    @close="handleDropdownClose"
    @triggerClick="handleTriggerClick"
  >
    <template #trigger>
      <slot name="trigger">
        <div class="secondary_small gap-1">
          <ThemeImage
            class="size-4"
            :icon="imageNames.ADD_TO_SEQUENCE_GREY"
            alt="add_to_sequence"
          />
          Add to {{ featureNames.SEQUENCES }}
          <ThemeImage
            class="size-4"
            :icon="imageNames.DROPDOWN_GREY"
            alt="dropdown"
          />
        </div>
      </slot>
    </template>

    <template #default>
      <div class="flex flex-col w-[266px]">
        <div class="flex flex-col max-h-[300px]">
          <div
            class="flex flex-col py-3 px-2"
            :class="{ border_bottom: items.length > 0 || loading }"
          >
            <span
              class="secondary_color font-semibold text-xs leading-[17px] px-3 pb-2"
            >
              CHOOSE A {{ featureNames.SEQUENCES.toUpperCase() }}
            </span>
            <CustomInput
              format="text"
              v-model="searchQuery"
              placeholder="Search here"
              type="primary"
              :icon="imageNames.SEARCH_VARIANT1"
              alt="search_icon"
              auto-focus
            />
          </div>

          <div
            v-if="items.length > 0"
            class="flex flex-col p-2 overflow-y-auto custom_scrollbar"
            @scroll="handleScroll"
          >
            <div
              v-for="(sequence, index) in items"
              :key="`${sequence.id}_seq_${index}`"
              class="dropdown_item flex-shrink-0 flex items-center flex-row gap-2 px-3 py-2 rounded-lg"
              @click="handleSequenceSelected(sequence)"
            >
              <div class="w-full flex flex-row items-center justify-between gap-1 text-sm">
                <div class="flex flex-col w-[65%] gap-1">
                  <span class="text_color truncate text-sm font-normal leading-[17px]">
                    {{ sequence?.name || "" }}
                  </span>
                </div>
                <div
                  :class="[
                    'px-2 flex items-center justify-center py-0 rounded-full',
                    sequence?.status === 'Active'
                      ? 'active_sequence_tag'
                      : 'inactive_sequence_tag',
                  ]"
                >
                  <span
                    :class="[
                      sequence?.status === 'Active'
                        ? 'text-white'
                        : 'text_color',
                      'text-[11px] font-semibold',
                    ]"
                  >
                    {{ sequence?.status === "Active" ? "Active" : "Inactive" }}
                  </span>
                </div>
              </div>
            </div>
            <div v-if="loadingMore" class="flex justify-center py-2">
              <CustomLoader :size="14" />
            </div>
          </div>

          <div v-else-if="loading" class="flex justify-center py-4">
            <CustomLoader :size="16" />
          </div>

          <div v-else-if="!loading && items.length === 0" class="flex justify-center w-full">
            <EmptyScreen
              source="sequence_dropdown_empty"
              text="No results found"
              css="size-12"
              padding="pt-6 pb-8"
            />
          </div>

          <div class="flex flex-col p-2 border_top">
            <div
              class="dropdown_item flex items-center flex-row gap-2 px-3 py-2 rounded-lg"
              @click="handleCreateSequenceClick"
            >
              + Create New {{ featureNames.SEQUENCES }}
            </div>
          </div>
        </div>
      </div>
    </template>
  </CustomDropdown>

  <CreateSequenceModal
    :width="'500px'"
    :title="`Create New ${featureNames.SEQUENCES}`"
    v-model:visible="showSequenceCreateModal"
    @sequence-created="handleSequenceCreatedFromModal"
    :add-action="true"
    :show-message="true"
    :navigate-after-creation="false"
  />
</template>

<script setup>
import { ref } from "vue";
import { message } from "ant-design-vue";
import CustomDropdown from "@/src/components/CustomDropdown.vue";
import CustomInput from "@/src/components/CustomInput.vue";
import CustomLoader from "@/src/components/CustomLoader.vue";
import CreateSequenceModal from "@/src/views/sequence/actions/CreateSequenceModal.vue";
import ThemeImage from "@/src/components/ThemeImage.vue";
import EmptyScreen from "@/src/components/EmptyScreen.vue";
import SequenceService from "@/src/api/SequenceService.js";
import { imageNames } from "@/src/constants/imageConstants.js";
import { featureNames } from "@/src/constants/globalConstants";
import { useInfiniteScroll } from "@/src/composables/useInfiniteScroll.js";

const props = defineProps({
  disabled: { type: Boolean, default: false },
  lazy: { type: Boolean, default: false },
});

const emit = defineEmits(["sequence-selected"]);

const dropdownRef = ref(null);
const showSequenceCreateModal = ref(false);

const fetchSequencesFn = async (page, pageSize, search) => {
  const response = await SequenceService.fetchSequences(page - 1, pageSize, search);
  return {
    items: response?.sequences || [],
    total: response?.total || 0,
  };
};

const {
  items,
  loading,
  loadingMore,
  searchQuery,
  handleScroll,
  refetch,
} = useInfiniteScroll({ fetchFn: fetchSequencesFn, pageSize: 15, debounceMs: 150, autoFetch: !props.lazy });

const handleSequenceSelected = (sequence) => {
  emit("sequence-selected", { id: sequence.id, name: sequence.name });
  dropdownRef.value?.closeDropdown?.();
};

const handleCreateSequenceClick = () => {
  showSequenceCreateModal.value = true;
  dropdownRef.value?.closeDropdown?.();
};

const handleSequenceCreatedFromModal = async ({ sequenceId, tags, sequenceType }) => {
  showSequenceCreateModal.value = false;
  
  if (sequenceId) {
    try {
      const sequenceResponse = await SequenceService.fetchSequenceDetails(sequenceId);
      if (sequenceResponse?.success && sequenceResponse?.sequence) {
        emit("sequence-selected", { id: sequenceResponse.sequence.id, name: sequenceResponse.sequence.name || null });
      } else {
        message.error(`Failed to fetch ${featureNames.SEQUENCES} details`);
      }
    } catch (error) {
      console.error("Error fetching sequence details:", error);
      message.error(`Failed to fetch ${featureNames.SEQUENCES} details`);
    }
  }
};

const handleDropdownClose = () => {
  searchQuery.value = "";
};

const handleTriggerClick = ({ isOpen }) => {
  if (!isOpen) {
    refetch();
  }
};
</script>
